/**
 * PermissionBoundary Component
 * Component-level permission check that conditionally renders content
 */

import React, { useEffect } from 'react';
import { useAuth } from '@/features/auth/contexts/EnhancedAuthContext';
import { logComponentAccess } from '../audit';
import { PermissionScope } from '@/types';

interface Permission {
  resource: string;
  action: string;
  scope?: PermissionScope;
}

interface PermissionBoundaryProps {
  children: React.ReactNode;
  require: Permission | Permission[];
  fallback?: React.ReactNode;
  auditDenied?: boolean;
}

export const PermissionBoundary: React.FC<PermissionBoundaryProps> = ({
  children,
  require,
  fallback = null,
  auditDenied = true
}) => {
  const { hasPermission } = useAuth();

  const requiredPerms = Array.isArray(require) ? require : [require];
  const hasAllPermissions = requiredPerms.every(perm =>
    hasPermission({
      resource: perm.resource,
      action: perm.action,
      scope: perm.scope || PermissionScope.SELF
    })
  );

  // Log denied access
  useEffect(() => {
    if (!hasAllPermissions && auditDenied) {
      logComponentAccess('PermissionBoundary', requiredPerms, false);
    }
  }, [requiredPerms, hasAllPermissions, auditDenied]);

  return hasAllPermissions ? <>{children}</> : <>{fallback}</>;
};
